import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import type { DeadlineTagVocabularyItem } from '@/hooks/useTagVocabulary';
import type { DeadlineTag } from '@/lib/deadlines/tags';
import { cn } from '@/lib/utils';
import { AddButton } from './add-button';
import { Command, CommandGroup, CommandItem, CommandList } from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

// Palette-name (from the shared taxonomy) -> chip classes for deadline tags.
// Classes are listed literally so Tailwind's JIT includes them.
const TAG_PALETTE: Record<string, string> = {
  rose: 'bg-rose-50 text-rose-700',
  amber: 'bg-amber-50 text-amber-700',
  slate: 'bg-slate-50 text-slate-600',
  teal: 'bg-teal-50 text-teal-700',
  violet: 'bg-violet-50 text-violet-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  orange: 'bg-orange-50 text-orange-700',
  sky: 'bg-sky-50 text-sky-700',
  indigo: 'bg-indigo-50 text-indigo-700',
  blue: 'bg-blue-50 text-blue-700',
  cyan: 'bg-cyan-50 text-cyan-700',
  lime: 'bg-lime-50 text-lime-700',
  fuchsia: 'bg-fuchsia-50 text-fuchsia-700',
  pink: 'bg-pink-50 text-pink-700',
  red: 'bg-red-50 text-red-700',
  yellow: 'bg-yellow-50 text-yellow-700',
  neutral: 'bg-slate-50 text-slate-500',
};

const TAG_DOT: Record<string, string> = {
  rose: 'bg-rose-400', amber: 'bg-amber-400', slate: 'bg-slate-400', teal: 'bg-teal-400',
  violet: 'bg-violet-400', emerald: 'bg-emerald-400', orange: 'bg-orange-400', sky: 'bg-sky-400',
  indigo: 'bg-indigo-400', blue: 'bg-blue-400', cyan: 'bg-cyan-400', lime: 'bg-lime-400',
  fuchsia: 'bg-fuchsia-400', pink: 'bg-pink-400', red: 'bg-red-400', yellow: 'bg-yellow-400',
  neutral: 'bg-slate-300',
};

interface DeadlineTagChipsProps {
  tags: DeadlineTag[];
  /** Controlled tag vocabulary, supplied by the page (not fetched per instance). */
  vocabulary: DeadlineTagVocabularyItem[];
  /** When false, chips are read-only: no remove, no "+" picker. */
  editable: boolean;
  onAdd: (label: string) => void;
  onRemove: (tag: DeadlineTag) => void;
  /**
   * Keep the "+" hidden until the enclosing `group/desccell` is hovered. Used by
   * the table and detail panel to stay uncluttered; forms show it permanently.
   */
  revealAddOnHover?: boolean;
  className?: string;
}

/**
 * Theme tag chips for a deadline: AI-classified (read-only) + user-managed
 * (manual, removable), plus a "+" picker over the controlled vocabulary.
 *
 * Purely presentational — the caller decides whether `onAdd`/`onRemove` write
 * straight through to the API (table, detail panel) or stage changes until a
 * form is saved (edit dialog).
 */
export function DeadlineTagChips({
  tags,
  vocabulary,
  editable,
  onAdd,
  onRemove,
  revealAddOnHover = false,
  className,
}: DeadlineTagChipsProps) {
  const [open, setOpen] = useState(false);

  const colorFor = (label: string): string =>
    TAG_PALETTE[vocabulary.find((v) => v.label === label)?.color ?? 'neutral'] ?? TAG_PALETTE.neutral;

  const presentLabels = new Set(tags.map((t) => t.label));
  const available = vocabulary.filter((v) => !presentLabels.has(v.label));
  // With no chips beside it a bare "+" reads as nothing at all, so forms label
  // it. Hover-reveal contexts stay compact: there the "+" is an affordance on an
  // already-populated row, not the only thing in the field.
  const showAddLabel = !revealAddOnHover && tags.length === 0;

  if (tags.length === 0 && !editable) return null;

  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {tags.map((tag) => {
        const chipClass = cn(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium opacity-80',
          colorFor(tag.label),
        );
        // Manual tags can be removed by clicking the whole chip; AI tags are
        // read-only, so they render as a plain (non-interactive) chip.
        if (tag.origin === 'manual' && editable) {
          return (
            <button
              key={tag.id || tag.label}
              type="button"
              className={cn(chipClass, 'group/tag transition-colors hover:brightness-95')}
              title="Click to remove tag"
              aria-label={`Remove ${tag.label} tag`}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(tag);
              }}
            >
              {tag.label}
              <X className="h-2.5 w-2.5 opacity-0 transition-opacity group-hover/tag:opacity-70" />
            </button>
          );
        }
        return (
          <span key={tag.id || tag.label} className={chipClass} title="AI-suggested tag">
            {tag.label}
          </span>
        );
      })}
      {editable && available.length > 0 && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            {/* Standing alone it is the section's only action, so it uses the
                same control as the other empty states; beside chips it stays a
                matching dashed chip. */}
            {showAddLabel ? (
              <AddButton label="Add tags" onClick={(e) => e.stopPropagation()} />
            ) : (
              <button
                type="button"
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-500 hover:bg-slate-50',
                  revealAddOnHover &&
                    'opacity-0 transition-opacity group-hover/desccell:opacity-100 focus:opacity-100',
                  revealAddOnHover && open && 'opacity-100',
                )}
                aria-label="Add tag"
                onClick={(e) => e.stopPropagation()}
              >
                <Plus className="h-2.5 w-2.5" />
              </button>
            )}
          </PopoverTrigger>
          <PopoverContent className="w-56 p-0" align="start" onClick={(e) => e.stopPropagation()}>
            <Command>
              <CommandList>
                <CommandGroup heading="Add tag">
                  {available.map((v) => (
                    <CommandItem
                      key={v.label}
                      value={v.label}
                      onSelect={() => {
                        setOpen(false);
                        onAdd(v.label);
                      }}
                    >
                      <span className={cn('mr-2 h-2 w-2 rounded-full', TAG_DOT[v.color] ?? TAG_DOT.neutral)} />
                      {v.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
